import { uid } from './bracket';
import { ligtVast } from './validate';
import type { Match, Player, Poule, PouleLid, Profile, Tournament } from './types';

/* ==========================================================================
   Demomodus — draait zonder database.

   Zodra VITE_SUPABASE_URL ontbreekt, werkt de app op deze verzonnen data. Dat
   is handig om te ontwikkelen en om de app te laten zien zonder dat er een
   project achter hangt. Wijzigingen blijven in localStorage staan, zodat een
   herlaadbeurt ze niet wist.
   ========================================================================== */

const OPSLAG = 'foosrankr-demo';

interface DemoData {
  players: Player[];
  matches: Match[];
  tournaments: Tournament[];
}

/** Kleine generator met vaste startwaarde, zodat de demo elke keer gelijk is. */
function maakRandom(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function verzin(): DemoData {
  const rnd = maakRandom(20260818);
  const namen = ['Imre', 'Sanne', 'Joost', 'Feline', 'Bram', 'Nadia', 'Tim', 'Eva'];
  /* Verborgen sterkte, alleen om geloofwaardige uitslagen te verzinnen. */
  const sterkte = [0.72, 0.66, 0.5, 0.61, 0.44, 0.55, 0.38, 0.58];

  const players: Player[] = namen.map((name, i) => ({
    id: uid(),
    name,
    username: name.toLowerCase(),
    bijnaam: null,
    bijnaamDoor: null,
    photo: '',
    isActive: i !== 7, // Eva is gestopt: uitslagen blijven staan
    createdAt: new Date(Date.now() - (200 - i) * 86400000).toISOString()
  }));

  const matches: Match[] = [];
  const nu = new Date();

  for (let dagenTerug = 190; dagenTerug >= 0; dagenTerug--) {
    const datum = new Date(nu.getTime() - dagenTerug * 86400000);
    const dag = datum.getDay();
    if (dag === 0 || dag === 6) continue; // in het weekend staat de tafel stil
    if (rnd() > 0.55) continue;

    const aantal = 1 + Math.floor(rnd() * 3);
    for (let i = 0; i < aantal; i++) {
      const tweeTegenTwee = rnd() < 0.3;
      const pot = players.slice();
      const gekozen: Player[] = [];
      while (gekozen.length < (tweeTegenTwee ? 4 : 2)) {
        gekozen.push(pot.splice(Math.floor(rnd() * pot.length), 1)[0]);
      }

      const kans = (p: Player) => sterkte[players.indexOf(p)];
      const kansA = tweeTegenTwee ? (kans(gekozen[0]) + kans(gekozen[1])) / 2 : kans(gekozen[0]);
      const kansB = tweeTegenTwee ? (kans(gekozen[2]) + kans(gekozen[3])) / 2 : kans(gekozen[1]);
      const aWint = rnd() < kansA / (kansA + kansB);
      const verliezer = Math.floor(rnd() * 9);

      const gespeeld = new Date(
        datum.getFullYear(),
        datum.getMonth(),
        datum.getDate(),
        12 + Math.floor(rnd() * 6),
        Math.floor(rnd() * 60)
      );
      if (gespeeld > nu) continue;

      matches.push({
        id: uid(),
        playedAt: gespeeld.toISOString(),
        mode: tweeTegenTwee ? '2v2' : '1v1',
        teamA: tweeTegenTwee ? [gekozen[0].id, gekozen[1].id] : [gekozen[0].id],
        teamB: tweeTegenTwee ? [gekozen[2].id, gekozen[3].id] : [gekozen[1].id],
        scoreA: aWint ? 10 : verliezer,
        scoreB: aWint ? verliezer : 10,
        tournamentId: null,
        /* De verzonnen historie is al beslecht; alleen wat jij nu invoert wacht. */
        status: 'bevestigd',
        akkoord: [],
        ingevoerdDoor: null,
        teltMee: true,
        createdAt: gespeeld.toISOString()
      });
    }
  }

  return { players, matches, tournaments: [] };
}

function lees(): DemoData {
  try {
    const raw = localStorage.getItem(OPSLAG);
    if (raw) return JSON.parse(raw) as DemoData;
  } catch {
    /* onleesbaar; opnieuw verzinnen */
  }
  const vers = verzin();
  schrijf(vers);
  return vers;
}

function schrijf(data: DemoData): void {
  try {
    localStorage.setItem(OPSLAG, JSON.stringify(data));
  } catch {
    /* vol of geblokkeerd; dan alleen in het geheugen */
  }
}

let data: DemoData | null = null;

function store(): DemoData {
  data ??= lees();
  return data;
}

function bewaar(): void {
  if (data) schrijf(data);
}

export const demoProfiel: Profile = {
  id: 'demo-beheerder',
  name: 'Demo-beheerder',
  username: 'demo',
  role: 'admin',
  status: 'actief',
  rating: 1200,
  photo: '',
  createdAt: new Date().toISOString(),
  lastLoginAt: new Date().toISOString(),
  lastActivityAt: new Date().toISOString()
};

/**
 * In demomodus ben je de eerste speler uit de verzonnen data: dan heeft Home
 * iets te laten zien. Buiten de demo is je account gewoon zelf de speler.
 */
export function demoProfielMetSpeler(): Profile {
  const eerste = store().players[0];
  return eerste
    ? { ...demoProfiel, id: eerste.id, name: eerste.name, photo: eerste.photo }
    : demoProfiel;
}

/** Zolang de andere kant heeft om te tekenen of de stand te betwisten. */
export const BEZWAARTERMIJN = 4 * 86400000;

/** Rond is het zodra er van elk team iemand getekend heeft; zie migratie 015. */
function rond(m: Match): boolean {
  if (m.tournamentId) return true;
  if (Date.now() - new Date(m.createdAt).getTime() > BEZWAARTERMIJN) return true;
  return m.teamA.some((id) => m.akkoord.includes(id)) && m.teamB.some((id) => m.akkoord.includes(id));
}

/*
 * Eén verzonnen poule. Sinds alles via een poule loopt, is de app zonder poule
 * half zichtbaar: het invoerscherm stuurt je weg en de ranglijst hangt eraan.
 * De demo heeft er dus ook één, met iedereen erin en de eerste als beheerder.
 */
export const DEMO_POULE = 'demo-poule';

/* In de demo is er maar een poule; de favorietknop schakelt die aan en uit. */
let demoFavoriet = true;

export const demo = {
  zetFavoriet: (_pouleId: string, favoriet: boolean) => {
    demoFavoriet = favoriet;
  },
  zetBijnaam: (spelerId: string, bijnaam: string) => {
    const s = store().players.find((p) => p.id === spelerId);
    if (!s) return;
    s.bijnaam = bijnaam.trim() || null;
    s.bijnaamDoor = s.bijnaam ? (store().players[0]?.id ?? null) : null;
    bewaar();
  },
  players: () => store().players.slice(),
  /* Oude opslag kent status en akkoord nog niet; die telde gewoon mee. */
  matches: () =>
    store().matches.map((m) => ({
      ...m,
      status: m.status ?? 'bevestigd',
      akkoord: m.akkoord ?? [],
      ingevoerdDoor: m.ingevoerdDoor ?? null,
      teltMee: m.teltMee ?? true
    })),
  tournaments: () => store().tournaments.slice(),

  poules: (): Poule[] => [
    {
      id: DEMO_POULE,
      naam: 'Kantine',
      omschrijving: 'De tafel naast de koffieautomaat.',
      prijs: 'eeuwige roem',
      aangemaaktDoor: store().players[0]?.id ?? null,
      gearchiveerd: false,
      createdAt: store().players[0]?.createdAt ?? new Date().toISOString()
    }
  ],

  pouleLeden: (): PouleLid[] =>
    store().players.map((p, i) => ({
      pouleId: DEMO_POULE,
      profielId: p.id,
      rol: i === 0 ? 'beheerder' : 'lid',
      favoriet: i === 0 && demoFavoriet,
      sinds: p.createdAt
    })),

  addPlayer(p: Player) {
    store().players.push(p);
    bewaar();
    return p;
  },

  updatePlayer(p: Player) {
    const lijst = store().players;
    const i = lijst.findIndex((x) => x.id === p.id);
    if (i >= 0) lijst[i] = p;
    bewaar();
    return p;
  },

  addMatch(m: Match) {
    store().matches.push(m);
    bewaar();
    return m;
  },

  /* Ook in de demo telt een uitslag pas als beide kanten hem erkennen. */
  bevestig(id: string, profielId: string) {
    const m = store().matches.find((x) => x.id === id);
    if (!m) return;
    if (!m.akkoord.includes(profielId)) m.akkoord.push(profielId);
    m.status = rond(m) ? 'bevestigd' : 'wacht';
    bewaar();
  },

  /* Vier dagen na het invoeren telt een uitslag vanzelf; zie migratie 017. */
  vervalWachtende(): number {
    const grens = Date.now() - BEZWAARTERMIJN;
    let n = 0;
    for (const m of store().matches) {
      if (m.status === 'wacht' && new Date(m.createdAt).getTime() < grens) {
        m.status = 'bevestigd';
        n++;
      }
    }
    if (n) bewaar();
    return n;
  },

  wijzigScore(
    velden: { id: string; scoreA: number; scoreB: number; playedAt?: string | null },
    profielId: string
  ) {
    const m = store().matches.find((x) => x.id === velden.id);
    if (!m) return;
    if (ligtVast(m)) throw new Error('Dit potje ligt vast: na een week staat de uitslag definitief.');
    m.scoreA = velden.scoreA;
    m.scoreB = velden.scoreB;
    if (velden.playedAt) m.playedAt = velden.playedAt;
    /* Wie het al goedkeurde, keurde iets anders goed. */
    m.akkoord = [...m.teamA, ...m.teamB].includes(profielId) ? [profielId] : [];
    /* En de klok begint opnieuw, zowel die van de bezwaartermijn als die van
       het vastleggen; zie migratie 019 en 020. */
    m.createdAt = new Date().toISOString();
    m.status = rond(m) ? 'bevestigd' : 'wacht';
    bewaar();
  },

  deleteMatch(id: string) {
    const m = store().matches.find((x) => x.id === id);
    if (m && ligtVast(m)) throw new Error('Dit potje ligt vast en kan niet meer verwijderd worden.');
    data = { ...store(), matches: store().matches.filter((x) => x.id !== id) };
    bewaar();
  },

  saveTournament(t: Tournament) {
    const lijst = store().tournaments;
    const i = lijst.findIndex((x) => x.id === t.id);
    if (i >= 0) lijst[i] = t;
    else lijst.push(t);
    bewaar();
  },

  deleteTournament(id: string) {
    data = {
      ...store(),
      tournaments: store().tournaments.filter((t) => t.id !== id),
      matches: store().matches.filter((m) => m.tournamentId !== id)
    };
    bewaar();
  },

  reset() {
    data = verzin();
    bewaar();
  }
};
